import React from 'react';
import styles from './index.module.less';
import cn from 'classnames';
import { CartItemType } from '@base/cart/interface';
import Iconfont from '@base/lib/Iconfont';
import Countdown, { RenderParamType } from '@base/lib/Countdown';
import { CartType } from '@base/cart/useCart';
import { isBonusProduct, getBonusProductPrice } from 'base/utils/product';
import PointsTag from '@base/snippets/PointsTag';
import { useStore, observer } from 'base/store';
import { IconPoints } from '@pro/common/IconPoints';
import { getTagConfig, getTagElStyles } from 'mod/product/utils';
import { IActivityCode } from 'base/interfacesV2/feature';
import { useTrans } from 'base/utils/i18n';
import { ICartGroup } from 'mod/cart/interface'
import { GROUP_TYPE_REDEEM_REWARDS } from 'mod/cart/constant'
import RewardCoinsItem from '@base/activity/redeemRewards/RewardCoinsItem';

type CartItemPriceProps = {
    className?: string;
    item: CartItemType;
    loadCart: CartType['loadCart'];
    group: ICartGroup;
};

const activityThemeMap = {
    'POINTS_MALL_FLASH_SALE': 'green',
    default: 'red',
};

const RealPrice: React.FC<CartItemType> = props => {
    const {
        purchaseType,
        pointsNum,
        variantPriceExchange,
        variantPriceExchangeDisplay,
        retailPriceExchangeDisplay,
        activity: {
            valid = false,
            code,
        }
    } = props;

    const price = Number(variantPriceExchange) > 0 ? variantPriceExchangeDisplay : 'FREE';

    if (purchaseType === 'POINTS') {
        return (
            <div className="real-price points-price flex flex-middle">
                <IconPoints className="points-price-icon"/>
                {pointsNum}
            </div>
        )
    }

    if (variantPriceExchangeDisplay !== retailPriceExchangeDisplay) {
        return (
            <div className="real-price deal-price">
                {valid && code == 'FLASHSALE' && <Iconfont className="icon-a-flashsale"/>}
                {price}
            </div>
        )
    }
    return (
        <div className="real-price shop-price">
            {price}
        </div>
    )
};

const DealerPrice: React.FC<CartItemType> = props => {
    const { dealerShopPriceExchangeDisplay } = props;
    return !dealerShopPriceExchangeDisplay ? null : (
        <div className="del-price">
            {dealerShopPriceExchangeDisplay}
        </div>
    );
};

const DelPrice: React.FC<CartItemType> = props => {
    const {
        purchaseType,
        variantPriceExchangeDisplay,
        retailPriceExchangeDisplay,
    } = props;

    if (purchaseType === 'POINTS' || variantPriceExchangeDisplay !== retailPriceExchangeDisplay) {
        return (
            <del className="del-price">
                {retailPriceExchangeDisplay}
            </del>
        )
    }
    return null
};

const PriceActivity: React.FC<CartItemType & {
    loadCart: CartItemPriceProps['loadCart']
}> = props => {
    const {
        activity: { // cart list 返回的活动信息
            valid = false,
            code = '',
            text = '',
            timestamp = 0,
        },
        loadCart
    } = props;
    const { t } = useTrans()

    if (!valid) {
        return null;
    }

    // 去掉中划线 小写key
    const _code = code?.replace('_', '')?.toLowerCase() as IActivityCode
    const tagConfig = getTagConfig(_code, 'cartTip')

    return (
        <div
            className={cn(
                'price-activity',
                styles['price-activity'],
                styles[activityThemeMap[code] || activityThemeMap.default]
            )}
        >
            <div
                className="price-activity-tag"
                style={{
                    ...getTagElStyles(tagConfig)?.titleStyle,
                    ...getTagElStyles(tagConfig)?.wrapStyle,
                }}
            >
                {tagConfig?.title || text}
            </div>

            <div
                style={getTagElStyles(tagConfig)?.descStyle}
            >
                {/* 长度过长 absolute解决 */}
                <Countdown
                    className="price-activity-countdown"
                    isShowDay={true}
                    endTime={Number(timestamp)}
                    render={(o: RenderParamType) => `${t('web_time_ends_in')} ${o.day}D : ${o.hour}H : ${o.minute}M : ${o.second}S`}
                    onEnd={() => {
                        typeof loadCart === 'function' && loadCart();
                    }}
                />
            </div>

        </div>
    )
};

const CartItemPrice: React.FC<CartItemPriceProps> = props => {
    const {
        className,
        loadCart,
        item,
        group
    } = props;

    const { userStore } = useStore();
    const isDealer = userStore.isDealer();

    // 兑换奖励（万圣节 南瓜
    if (group?.groupType === GROUP_TYPE_REDEEM_REWARDS) {
        const { rewardCoin } = item
        return (
            <RewardCoinsItem
                iconUrl={rewardCoin.icon}
                num={rewardCoin.num}
            />
        )
    }

    if (isBonusProduct(item)) {
        return (
            <div className={cn(className, styles.prices)}>
                <div className="real-price deal-price">
                    {getBonusProductPrice(item)}
                </div>
            </div>
        )
    }

    return (
        <div className={cn(className, styles.prices)}>
            <RealPrice {...item}/>

            {isDealer && <DealerPrice {...item}/>}
            <DelPrice {...item}/>
            {!isDealer && (
                <>
                    <PriceActivity {...item} loadCart={loadCart}/>
                    <PointsTag
                        className={styles.points}
                        hasBg={true}
                        points={item.points}
                    />
                </>
            )}
        </div>
    );
};

export default observer(CartItemPrice);